import { useGetAllDepartmentsQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import { useGetPendingAssessmentsQuery } from '@/app/features/recruitment/evaluation/evaluation.api';
import { useGetAllJobPostsQuery } from '@/app/features/recruitment/job-applicants/job-applicants.api';
import EmptyState from '@/components/EmptyState';
import ViewToggler from '@/components/ViewToggler';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters, useSelectionOptions } from '@/hooks';
import { AssignmentsIcon, ChevronDownIcon, SearchIcon } from '@/icons';
import { cn } from '@/lib/cn';
import { Col, Flex, Input, Pagination, Row, Select } from 'antd';
import { useState } from 'react';
import EmpCard from './_components/EvAssessment/EmpCard';
import EmpCardSkeleton from './_components/EvAssessment/EmpCardSkeleton';
import GiveMark from './_components/EvAssessment/GiveMark';

const EvAssessment = () => {
  const [viewMode, setViewMode] = useState('grid');
  const {
    skip,
    page,
    limit,
    filters,
    keyword,
    handleFilter,
    handleSearch,
    handlePagination,
  } = useAppFilters(FILTER_KEYS.EV_ASSESSMENT, { defaultLimit: 12 });

  const [jobOptions] = useSelectionOptions(useGetAllJobPostsQuery, {
    labelKey: 'job_title',
    valueKey: 'id',
  });

  const [departmentOptions] = useSelectionOptions(useGetAllDepartmentsQuery, {
    labelKey: 'name',
    valueKey: 'id',
  });

  const {
    isLoading,
    isFetching,
    data: assessmentsData,
  } = useGetPendingAssessmentsQuery({
    params: {
      skip,
      limit,
      ...filters,
      search: keyword,
    },
  });

  const [config, handleOpen, handleClose] = useAppDiscloser(
    DISCLOSER_KEYS.EV_ASSESSMENT,
  );

  const pendingAssessments = assessmentsData?.data?.data || [];
  const total = assessmentsData?.data?.meta?.total || 0;

  return (
    <div>
      <Flex
        align="center"
        justify="space-between"
        className="flex-wrap lg:flex-nowrap gap-4 my-6"
      >
        <ViewToggler disabled="list" mode={viewMode} setMode={setViewMode} />
        <Flex align="center" gap={16} className="flex-wrap lg:flex-nowrap">
          <Input
            size="large"
            placeholder="Search"
            onChange={handleSearch}
            style={{ minWidth: 250 }}
            prefix={<SearchIcon size={20} className="text-white" />}
          />
          <Select
            allowClear
            showSearch
            size="large"
            options={jobOptions}
            placement="bottomRight"
            placeholder="Job Title"
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(val: string) => handleFilter('job_id', val)}
          />
          <Select
            allowClear
            size="large"
            placeholder="Department"
            options={departmentOptions}
            showSearch
            optionFilterProp="label"
            popupMatchSelectWidth={false}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            onChange={(val: string) => handleFilter('department_id', val)}
          />
          {/* <Select
            allowClear
            size="large"
            placeholder="Status"
            style={{ minWidth: 120 }}
            suffixIcon={<ChevronDownIcon size={24} className="text-white" />}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            onChange={(val) => handleFilter('status', val)}
          /> */}
        </Flex>
      </Flex>

      <Row gutter={[24, 24]} className="mb-6">
        {isLoading || isFetching ? (
          Array.from({ length: 8 }).map((_, idx) => (
            <Col key={idx} xs={24} md={12} lg={8} xl={6}>
              <EmpCardSkeleton />
            </Col>
          ))
        ) : (
          <>
            {pendingAssessments.map((item) => {
              const status = item?.assessment_status;

              return (
                <Col key={item.assignment_id} xs={24} md={12} lg={8} xl={6}>
                  <EmpCard
                    item={{
                      name: item.applicant_name,
                      photo: item.applicant_photo,
                      designation: item.job_title,
                      department: item.department_name,
                      assessment_status: item.assessment_status,
                    }}
                    classNames={{
                      titleWrapper: 'items-center',
                    }}
                    actionRender={
                      <button
                        onClick={() => {
                          if (status === 'submitted') {
                            handleOpen({ data: { id: item.assignment_id } });
                          }
                        }}
                        className={cn(
                          'text-theme-primary font-medium underline underline-offset-4 cursor-auto',
                          status === 'submitted' && 'cursor-pointer',
                          status === 'in_progress' && 'opacity-50',
                        )}
                      >
                        {status === 'in_progress' || status === 'submitted'
                          ? 'Assess'
                          : item.is_evaluated &&
                              item.score !== null &&
                              item.total_mark
                            ? `${Math.round((item.score / item.total_mark) * 100)}%`
                            : 'N/A'}
                      </button>
                    }
                  />
                </Col>
              );
            })}
            {pendingAssessments.length === 0 && (
              <Col span={24}>
                <EmptyState
                  icon={<AssignmentsIcon size={40} />}
                  title="No Pending Assessments"
                  description="All assessments have been evaluated, or none have been assigned yet."
                />
              </Col>
            )}
          </>
        )}
      </Row>

      {total > limit && (
        <Flex justify="end" className="mt-4">
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            onChange={handlePagination}
            showSizeChanger={false}
          />
        </Flex>
      )}

      <GiveMark
        open={config.open}
        handleClose={handleClose}
        id={config.data?.id}
      />
    </div>
  );
};

export default EvAssessment;
